// #786 — Mobile Configurations → Wake word tab.
// Same rules as the backend (BAL/BAL/AppBAL/LHB/WakeWordNormalizer.cs) — keep the two in step:
// a word is trimmed, every run of whitespace (tab, newline, full-width space…) becomes one space,
// it is stored in lower case, and duplicates are compared after that.

export const WAKE_WORD_MAX_LENGTH = 100;
export const WAKE_WORD_MAX_COUNT = 1000;

export function normalizeWakeWord(word) {
  return String(word ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Text typed or pasted into the add box → raw words (one per line, or separated by commas) */
export function splitWakeWordInput(text) {
  return String(text ?? '').split(/[\n,]/);
}

/**
 * Adds the words in `input` to `existing` (already normalized).
 * Returns the new list plus what happened, so the tab can say
 * "added 10 · skipped 2 duplicates · skipped 1 too long".
 */
export function mergeWakeWords(existing = [], input = []) {
  const words = [...existing];
  const seen = new Set(words);
  const added = [];
  let duplicates = 0;
  let tooLong = 0;
  let overLimit = 0;

  input.forEach((raw) => {
    const word = normalizeWakeWord(raw);
    if (!word) return;
    if (seen.has(word)) {
      duplicates += 1;
      return;
    }
    if (word.length > WAKE_WORD_MAX_LENGTH) {
      tooLong += 1;
      return;
    }
    if (words.length >= WAKE_WORD_MAX_COUNT) {
      overLimit += 1;
      return;
    }
    seen.add(word);
    words.push(word);
    added.push(word);
  });

  return { words, added, duplicates, tooLong, overLimit };
}

/**
 * Checks an in-place edit of words[index]. Returns { word } when it can replace the old one,
 * or { error: 'empty' | 'duplicate' | 'tooLong' }.
 */
export function validateWakeWordEdit(words = [], index, value) {
  const word = normalizeWakeWord(value);
  if (!word) return { error: 'empty' };
  if (word.length > WAKE_WORD_MAX_LENGTH) return { error: 'tooLong' };
  if (words.some((other, otherIndex) => otherIndex !== index && other === word)) return { error: 'duplicate' };
  return { word };
}
